import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone,
  Plus,
  Edit2,
  Trash2,
  CheckCircle,
  AlertCircle,
  MessageSquare
} from 'lucide-react';
import api, { TokenStorage } from '../../utils/api';
import AppShell from '../layout/AppShell';
import LoadingSpinner from '../common/LoadingSpinner';
import { SkeletonList } from '../common/SkeletonLoader';
import AddPhoneModal from '../dashboard/AddPhoneModal';
import DeletePhoneModal from '../dashboard/DeletePhoneModal';
import EditPhoneModal from '../dashboard/EditPhoneModal';
import { clearNotificationCache } from '../layout/NotificationBell';

// ─── Constants ───────────────────────────────────────────────────────────────

// Common US timezones with friendly names (from AccountPanel)
const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (New York)' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (No DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
];

const DAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const TABS = [
  { key: 'profile', label: 'Profile & Security' },
  { key: 'phones', label: 'Phone Numbers' },
  { key: 'alerts', label: 'Alert Preferences' },
];

// ─── Main Component ──────────────────────────────────────────────────────────

export default function SettingsPanel() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');

  const handleLogout = async () => {
    try {
      let useCookieFlow = false;
      let csrfToken = '';
      try {
        useCookieFlow = localStorage.getItem('auth_mode') === 'cookie';
        csrfToken = localStorage.getItem('csrfToken');
      } catch (e) {
        // Safari private browsing or storage unavailable
      }
      if (useCookieFlow) {
        await api.post('/api/auth/logout', {}, { headers: { 'X-CSRF-Token': csrfToken }, withCredentials: true });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      clearNotificationCache();
      TokenStorage.removeToken();
      navigate('/logged-out', { replace: true });
    }
  };

  return (
    <AppShell onLogout={handleLogout}>
      <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
        {/* Page Title */}
        <h1 className="text-xl sm:text-2xl font-bold font-display text-gray-900">Settings</h1>

        {/* Tab Bar */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-6 sm:space-x-8 overflow-x-auto" aria-label="Settings tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`whitespace-nowrap pb-3 text-sm font-medium border-b-2 transition-colors min-h-[44px] sm:min-h-0 ${
                  activeTab === tab.key
                    ? 'text-emerald-700 border-emerald-700'
                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'profile' && <ProfileSecurityTab />}
        {activeTab === 'phones' && <PhoneNumbersTab />}
        {activeTab === 'alerts' && <AlertPreferencesTab />}
      </div>
    </AppShell>
  );
}

// ─── Tab 1: Profile & Security ───────────────────────────────────────────────

function ProfileSecurityTab() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Timezone editing
  const [editingTimezone, setEditingTimezone] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [detectedTimezone, setDetectedTimezone] = useState('');

  // Password change
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Messages
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setDetectedTimezone(tz);
  }, []);

  async function loadData() {
    try {
      const userRes = await api.get('/api/user/me');
      const userData = userRes.data?.user || userRes.data;
      setUser(userData);
      setSelectedTimezone(userData.timezone || 'UTC');
    } catch (err) {
      console.error('Failed to load account data:', err);
      setError('Failed to load account information');
    } finally {
      setLoading(false);
    }
  }

  async function handleTimezoneUpdate() {
    setError('');
    setSuccess('');
    try {
      await api.put('/api/user/me/timezone', { timezone: selectedTimezone });
      setSuccess('Timezone updated successfully!');
      setEditingTimezone(false);
      await loadData();
    } catch (err) {
      console.error('Failed to update timezone:', err);
      setError(err?.response?.data?.error || 'Failed to update timezone');
    }
  }

  function handleAutoDetectTimezone() {
    setSelectedTimezone(detectedTimezone);
  }

  async function handlePasswordChange(e) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      await api.put('/api/user/me/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      setSuccess('Password changed successfully!');
      setChangingPassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      console.error('Failed to change password:', err);
      setError(err?.response?.data?.error || 'Failed to change password');
    }
  }

  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  function getTimezoneFriendlyName(tz) {
    const match = COMMON_TIMEZONES.find(t => t.value === tz);
    return match ? match.label : tz;
  }

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading your account settings..." />;
  }

  if (!user) {
    return (
      <div className="rounded-lg bg-red-50 p-4 border border-red-200">
        <div className="text-red-600">Failed to load account information</div>
      </div>
    );
  }

  const isOAuth = !!user.oauth_provider;
  const isLocal = !isOAuth;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 flex items-center justify-between gap-2">
          <span className="text-sm sm:text-base">{success}</span>
          <button onClick={() => setSuccess('')} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-green-700 hover:text-green-900 font-medium">
            X
          </button>
        </div>
      )}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 flex items-center justify-between gap-2">
          <span className="text-sm sm:text-base">{error}</span>
          <button onClick={() => setError('')} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-red-700 hover:text-red-900 font-medium">
            X
          </button>
        </div>
      )}

      {/* Account Information Section */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6 text-gray-900">Account Information</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
              Signed in as:
            </label>
            <p className="text-gray-900">{user.email}</p>
          </div>

          <div>
            <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
              Provider:
            </label>
            <div className="mt-1">
              {isOAuth ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {user.oauth_provider.charAt(0).toUpperCase() + user.oauth_provider.slice(1)}
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  Password Protected
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
              Member Since:
            </label>
            <p className="text-gray-900">{formatDate(user.created_at)}</p>
          </div>
        </div>
      </div>

      {/* Timezone Settings Section */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Time Zone</h2>
          {!editingTimezone && (
            <button
              onClick={() => setEditingTimezone(true)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium min-h-[44px] flex items-center px-2"
            >
              Edit
            </button>
          )}
        </div>

        {!editingTimezone ? (
          <div className="space-y-4">
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                Current Time Zone:
              </label>
              <p className="text-gray-900">{getTimezoneFriendlyName(user.timezone)}</p>
            </div>
            <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <span className="font-medium">Note:</span> Your timezone is used to schedule notifications at the correct local time, including daylight saving time adjustments.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">
                Select Time Zone
              </label>
              <select
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
                className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {COMMON_TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>

            {detectedTimezone && detectedTimezone !== selectedTimezone && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="text-sm text-yellow-800 mb-2">
                  Your browser detected: <strong>{getTimezoneFriendlyName(detectedTimezone)}</strong>
                </div>
                <button
                  onClick={handleAutoDetectTimezone}
                  className="text-sm text-yellow-700 hover:text-yellow-900 font-medium underline min-h-[44px] sm:min-h-0 inline-flex items-center"
                >
                  Use detected timezone
                </button>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleTimezoneUpdate}
                className="flex-1 min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 sm:py-2 rounded-lg transition-colors"
              >
                Save Changes
              </button>
              <button
                onClick={() => {
                  setEditingTimezone(false);
                  setSelectedTimezone(user.timezone);
                  setError('');
                }}
                className="flex-1 min-h-[44px] bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-4 py-2.5 sm:py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Security Section - Only for Local Users */}
      {isLocal && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Security</h2>
            {!changingPassword && (
              <button
                onClick={() => setChangingPassword(true)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium min-h-[44px] flex items-center px-2"
              >
                Change Password
              </button>
            )}
          </div>

          {!changingPassword ? (
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                Password:
              </label>
              <p className="text-gray-600 text-sm">Your password is securely encrypted. Last changed: {formatDate(user.updated_at)}</p>
            </div>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  minLength={8}
                  required
                />
                <p className="mt-1 text-sm text-gray-500">Must be at least 8 characters long</p>
              </div>

              <div>
                <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 sm:py-2 rounded-lg transition-colors"
                >
                  Update Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setChangingPassword(false);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setError('');
                  }}
                  className="flex-1 min-h-[44px] bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-4 py-2.5 sm:py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* OAuth Info Section */}
      {isOAuth && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6 text-gray-900">Security</h2>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <div className="font-medium text-blue-900">Your account is secured with {user.oauth_provider}</div>
                <div className="text-sm text-blue-700 mt-1">
                  Password management is handled through your {user.oauth_provider} account. You don't need to set a password here.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab 2: Phone Numbers ────────────────────────────────────────────────────

function PhoneNumbersTab() {
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [notificationCounts, setNotificationCounts] = useState({});

  useEffect(() => {
    loadPhones();
  }, []);

  async function loadPhones() {
    try {
      setLoading(true);
      const resp = await api.get('/api/sms/phones');
      const phoneList = resp.data?.phones || [];
      setPhones(phoneList);

      const counts = {};
      await Promise.all(
        phoneList.map(async (phone) => {
          try {
            const countResp = await api.get(`/api/sms/phones/${phone.id}/notifications-count`);
            counts[phone.id] = countResp.data?.count || 0;
          } catch (err) {
            counts[phone.id] = 0;
          }
        })
      );
      setNotificationCounts(counts);
      setError('');
    } catch (err) {
      console.error('Error loading phones:', err);
      setError('Failed to load phone numbers');
    } finally {
      setLoading(false);
    }
  }

  function handleAddClick() {
    setShowAddModal(true);
  }

  function handleEditClick(phone) {
    setSelectedPhone(phone);
    setShowEditModal(true);
  }

  function handleDeleteClick(phone) {
    setSelectedPhone(phone);
    setShowDeleteModal(true);
  }

  function handleAddSuccess() {
    setShowAddModal(false);
    loadPhones();
  }

  function handleEditSuccess() {
    setShowEditModal(false);
    setSelectedPhone(null);
    loadPhones();
  }

  function handleDeleteSuccess() {
    setShowDeleteModal(false);
    setSelectedPhone(null);
    loadPhones();
  }

  function handleModalClose() {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedPhone(null);
  }

  function formatPhoneNumber(phoneNumber) {
    if (!phoneNumber) return '';
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.startsWith('1') && cleaned.length === 11) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phoneNumber;
  }

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Phone Numbers</h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Manage phone numbers for SMS notifications</p>
          </div>
          <div className="w-full sm:w-32 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>
        <SkeletonList count={2} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 border border-red-200">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Phone Numbers</h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage phone numbers for SMS notifications</p>
        </div>
        <button
          onClick={handleAddClick}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm sm:text-base rounded-lg hover:bg-blue-700 transition-colors shadow-sm w-full sm:w-auto min-h-[44px] sm:min-h-0"
        >
          <Plus className="w-4 h-4" />
          Add Phone
        </button>
      </div>

      {phones.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-12">
          <div className="text-center max-w-md mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
              <Phone className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Add your first phone number
            </h3>
            <p className="text-gray-600 mb-6">
              Add a verified phone number to start receiving SMS notifications about your account balances.
            </p>
            <button
              onClick={handleAddClick}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm min-h-[44px] sm:min-h-0"
            >
              <Plus className="w-5 h-5" />
              Add Phone Number
            </button>
          </div>
        </div>
      )}

      {phones.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
          {phones.map((phone) => (
            <div
              key={phone.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100">
                    <Phone className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{phone.phone_name}</h3>
                    <p className="text-sm text-gray-600">{formatPhoneNumber(phone.phone_number)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEditClick(phone)}
                    className="p-2.5 sm:p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit label"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(phone)}
                    className="p-2.5 sm:p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove phone"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">Verified</span>
                </div>

                {notificationCounts[phone.id] !== undefined && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <MessageSquare className="w-4 h-4" />
                    <span className="text-sm">
                      {notificationCounts[phone.id]} notification{notificationCounts[phone.id] !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddPhoneModal
          onClose={handleModalClose}
          onSuccess={handleAddSuccess}
        />
      )}

      {showEditModal && selectedPhone && (
        <EditPhoneModal
          phone={selectedPhone}
          onClose={handleModalClose}
          onSuccess={handleEditSuccess}
        />
      )}

      {showDeleteModal && selectedPhone && (
        <DeletePhoneModal
          phone={selectedPhone}
          notificationCount={notificationCounts[selectedPhone.id] || 0}
          onClose={handleModalClose}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
}

// ─── Tab 3: Alert Preferences ────────────────────────────────────────────────

function AlertPreferencesTab() {
  const [prefs, setPrefs] = useState(null);
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');

  const loadPreferences = useCallback(async () => {
    try {
      const [prefsRes, histRes, phonesRes] = await Promise.all([
        api.get('/api/alert-preferences'),
        api.get('/api/alert-preferences/history', { params: { limit: 5 } }),
        api.get('/api/sms/phones'),
      ]);
      setPrefs(prefsRes.data.preferences);
      setHistory(histRes.data.runs || []);
      setPhones((phonesRes.data?.phones || []).filter(p => p.is_confirmed));
      setError('');
    } catch (err) {
      setError('Failed to load alert preferences');
      console.error('Failed to load alert preferences', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const updatePref = async (field, value) => {
    setSaving(true);
    try {
      const { data } = await api.put('/api/alert-preferences', { [field]: value });
      setPrefs(data.preferences);
      setError('');
    } catch (err) {
      setError('Failed to save preference');
      console.error('Failed to update preference', err);
    } finally {
      setSaving(false);
    }
  };

  const handleResetFailures = async () => {
    try {
      await api.post('/api/alert-preferences/reset-failures');
      await loadPreferences();
    } catch (err) {
      setError('Failed to reset failures');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <p className="text-gray-500 text-base sm:text-sm">Loading alert preferences...</p>
      </div>
    );
  }

  if (!prefs) {
    return (
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <p className="text-red-600 text-base sm:text-sm">{error || 'Unable to load preferences'}</p>
      </div>
    );
  }

  const hasFailures = (prefs.daily_consecutive_failures > 0) || (prefs.weekly_consecutive_failures > 0);

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">SMS Alerts</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-base sm:text-sm">
          {error}
        </div>
      )}

      {/* Phone selection */}
      {phones.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-base sm:text-sm font-medium text-gray-900">Send to</label>
              <p className="text-base sm:text-sm text-gray-500">Phone number for digest alerts</p>
            </div>
            <select
              value={prefs.phone_id ?? ''}
              onChange={(e) => updatePref('phone_id', e.target.value ? parseInt(e.target.value) : null)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-base sm:text-sm min-h-[44px]"
              disabled={saving}
            >
              <option value="">Auto (most recent)</option>
              {phones.map(p => (
                <option key={p.id} value={p.id}>
                  {p.phone_name ? `${p.phone_name} (${p.phone_number})` : p.phone_number}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Daily Digest Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base sm:text-sm font-medium text-gray-900">Daily Digest</h3>
            <p className="text-base sm:text-sm text-gray-500">Morning summary of bills and balances</p>
          </div>
          <ToggleSwitch
            enabled={prefs.daily_digest_enabled}
            onChange={(val) => updatePref('daily_digest_enabled', val)}
            disabled={saving}
          />
        </div>

        {prefs.daily_digest_enabled && (
          <div className="ml-0 sm:ml-4 space-y-3 border-l-2 border-gray-100 pl-4">
            <div className="flex items-center justify-between">
              <label className="text-base sm:text-sm text-gray-700">Send time</label>
              <input
                type="time"
                value={prefs.daily_digest_time?.substring(0, 5) || '09:00'}
                onChange={(e) => updatePref('daily_digest_time', e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-base sm:text-sm min-h-[44px]"
                disabled={saving}
              />
            </div>

            <ToggleRow
              label="Include overdue items"
              enabled={prefs.include_overdue}
              onChange={(val) => updatePref('include_overdue', val)}
              disabled={saving}
            />
            <ToggleRow
              label="Include due today"
              enabled={prefs.include_due_today}
              onChange={(val) => updatePref('include_due_today', val)}
              disabled={saving}
            />
            <ToggleRow
              label="Include due this week"
              enabled={prefs.include_due_this_week}
              onChange={(val) => updatePref('include_due_this_week', val)}
              disabled={saving}
            />
            <ToggleRow
              label="Include income preview"
              enabled={prefs.include_income_preview}
              onChange={(val) => updatePref('include_income_preview', val)}
              disabled={saving}
            />
            <ToggleRow
              label="Low balance warning"
              enabled={prefs.include_balance_warning}
              onChange={(val) => updatePref('include_balance_warning', val)}
              disabled={saving}
            />

            {prefs.include_balance_warning && (
              <div className="flex items-center justify-between">
                <label className="text-base sm:text-sm text-gray-700">Warning threshold</label>
                <div className="flex items-center">
                  <span className="text-base sm:text-sm text-gray-500 mr-1">$</span>
                  <input
                    type="number"
                    min="0"
                    step="10"
                    value={parseFloat(prefs.balance_warning_threshold) || 100}
                    onChange={(e) => updatePref('balance_warning_threshold', parseFloat(e.target.value) || 0)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-base sm:text-sm w-24 min-h-[44px]"
                    disabled={saving}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Weekly Recap Section */}
      <div className="mb-6 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base sm:text-sm font-medium text-gray-900">Weekly Recap</h3>
            <p className="text-base sm:text-sm text-gray-500">Week summary and preview</p>
          </div>
          <ToggleSwitch
            enabled={prefs.weekly_recap_enabled}
            onChange={(val) => updatePref('weekly_recap_enabled', val)}
            disabled={saving}
          />
        </div>

        {prefs.weekly_recap_enabled && (
          <div className="ml-0 sm:ml-4 space-y-3 border-l-2 border-gray-100 pl-4">
            <div className="flex items-center justify-between">
              <label className="text-base sm:text-sm text-gray-700">Day</label>
              <select
                value={prefs.weekly_recap_day ?? 0}
                onChange={(e) => updatePref('weekly_recap_day', parseInt(e.target.value))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-base sm:text-sm min-h-[44px]"
                disabled={saving}
              >
                {DAYS.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-base sm:text-sm text-gray-700">Time</label>
              <input
                type="time"
                value={prefs.weekly_recap_time?.substring(0, 5) || '18:00'}
                onChange={(e) => updatePref('weekly_recap_time', e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-base sm:text-sm min-h-[44px]"
                disabled={saving}
              />
            </div>
          </div>
        )}
      </div>

      {/* Segment usage */}
      <div className="pt-4 border-t border-gray-200 mb-4">
        <p className="text-base sm:text-sm text-gray-500">
          SMS segments used this month: {prefs.sms_segments_sent_month || 0} / 120
        </p>
      </div>

      {/* Failure reset */}
      {hasFailures && (
        <div className="pt-4 border-t border-gray-200 mb-4">
          <div className="bg-yellow-50 rounded-lg p-3">
            <p className="text-base sm:text-sm text-yellow-800 mb-2">
              Digest delivery has failed {prefs.daily_consecutive_failures + prefs.weekly_consecutive_failures} time(s).
              {(prefs.daily_consecutive_failures >= 5 || prefs.weekly_consecutive_failures >= 5) &&
                ' Automatic sending has been paused.'}
            </p>
            <button
              onClick={handleResetFailures}
              className="px-4 py-2 min-h-[44px] bg-yellow-600 text-white text-base sm:text-sm font-medium rounded-lg hover:bg-yellow-700 transition-colors"
            >
              Reset and re-enable
            </button>
          </div>
        </div>
      )}

      {/* Recent history */}
      {history.length > 0 && (
        <div className="pt-4 border-t border-gray-200">
          <h3 className="text-base sm:text-sm font-medium text-gray-900 mb-2">Recent Sends</h3>
          <div className="space-y-1">
            {history.map(run => (
              <div key={run.id} className="flex items-center justify-between text-base sm:text-sm">
                <span className="text-gray-500">
                  {run.digest_type === 'daily' ? 'Daily' : 'Weekly'} --{' '}
                  {new Date(run.created_at).toLocaleDateString()}
                </span>
                <span className={`font-medium ${
                  run.status === 'sent' ? 'text-green-600' :
                  run.status === 'failed' ? 'text-red-600' :
                  'text-gray-500'
                }`}>
                  {run.status} {run.segments_used > 0 && `(${run.segments_used} seg)`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function ToggleSwitch({ enabled, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => onChange(!enabled)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 min-w-[44px] min-h-[44px] items-center ${
        enabled ? 'bg-emerald-600' : 'bg-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function ToggleRow({ label, enabled, onChange, disabled }) {
  return (
    <div className="flex items-center justify-between">
      <label className="text-base sm:text-sm text-gray-700">{label}</label>
      <ToggleSwitch enabled={enabled} onChange={onChange} disabled={disabled} />
    </div>
  );
}
